import * as XLSX from 'xlsx';
import {
  extractEmail,
  normalizeSchedulingKey,
  normalizeSchedulingText,
} from '../ticketing/partner-scheduling-patterns.js';

export const PARTNER_CONTACT_INFORMATION = 'partner contact information';

const PRIMARY_SECTION = 'primary';
const ESCALATION_FALLBACK = ['escalation', 'escalation 2', 'escalation 3'] as const;

type Contact = {
  name: string | null;
  email: string | null;
  phone: string | null;
};

type PartnerContactColumns = {
  headerRowIndex: number;
  sectionColumnIndex: number;
  labelColumnIndex: number;
  valueColumnIndex: number;
};

export function normalizeSchedulingPartnerName(name: string): string {
  return normalizeSchedulingText(name)
    .replace(/^Partner\s*-?\s*/i, '')
    .replace(/\s*\(.*\)/, '')
    .trim();
}

function isEmailLabel(label: string) {
  return label === 'e mail' || label === 'email';
}

function isPhoneLabel(label: string) {
  return label.includes('telephone') || label === 'mobile number' || label === 'mobile';
}

function isFieldLabel(label: string) {
  return label === 'name' || label === 'position' || isEmailLabel(label) || isPhoneLabel(label);
}

/** Block markers in the section column (note text in merged cells is ignored). */
function parseBlockSection(raw: string): string | null {
  const key = normalizeSchedulingKey(raw);
  if (key === PRIMARY_SECTION || key === 'and cc') return key;
  if (key === 'escalation' || key.startsWith('escalation ')) return key;
  return null;
}

function parseWorkbookEmail(value: string) {
  const cleaned = String(value ?? '').replace(/@{2,}/g, '@');
  return extractEmail(cleaned);
}

function scoreContactColumns(
  rows: Array<Array<string | number | null>>,
  fromRow: number,
  toRow: number,
  sectionCol: number,
  labelCol: number,
  valueCol: number,
) {
  let score = 0;
  for (let i = fromRow; i < toRow; i++) {
    const row = rows[i] ?? [];
    const section = normalizeSchedulingKey(String(row[sectionCol] ?? ''));
    const label = normalizeSchedulingKey(String(row[labelCol] ?? ''));
    const value = String(row[valueCol] ?? '').trim();
    if (section === PRIMARY_SECTION) score += 2;
    if (!isFieldLabel(label)) continue;
    score += 3;
    if (isEmailLabel(label) && parseWorkbookEmail(value)) score += 5;
    else if (value) score += 1;
  }
  return score;
}

function findPartnerContactColumns(rows: Array<Array<string | number | null>>): PartnerContactColumns | null {
  const headerRowIndex = rows.findIndex(row =>
    row.some(cell =>
      normalizeSchedulingKey(String(cell ?? '')).includes(normalizeSchedulingKey(PARTNER_CONTACT_INFORMATION)),
    ),
  );
  if (headerRowIndex < 0) return null;

  const partnerContactCol =
    rows[headerRowIndex]?.findIndex(cell =>
      normalizeSchedulingKey(String(cell ?? '')).includes(normalizeSchedulingKey(PARTNER_CONTACT_INFORMATION)),
    ) ?? -1;
  if (partnerContactCol < 0) return null;

  const fromRow = headerRowIndex + 1;
  const toRow = Math.min(rows.length, headerRowIndex + 40);
  let best: PartnerContactColumns | null = null;
  let bestScore = 0;

  for (let offset = 0; offset <= 2; offset++) {
    const sectionColumnIndex = partnerContactCol + offset;
    const labelColumnIndex = sectionColumnIndex + 1;
    const valueColumnIndex = labelColumnIndex + 1;
    const score = scoreContactColumns(rows, fromRow, toRow, sectionColumnIndex, labelColumnIndex, valueColumnIndex);
    if (score > bestScore) {
      bestScore = score;
      best = { headerRowIndex, sectionColumnIndex, labelColumnIndex, valueColumnIndex };
    }
  }

  return bestScore >= 5 ? best : null;
}

function expandMergedCells(rows: Array<Array<string | number | null>>, sheet: XLSX.WorkSheet) {
  const filled = rows.map(row => [...row]);
  for (const { s, e } of sheet['!merges'] ?? []) {
    const master = filled[s.r]?.[s.c];
    if (master == null || master === '') continue;
    for (let r = s.r; r <= e.r; r++) {
      for (let c = s.c; c <= e.c; c++) {
        const row = filled[r] ?? [];
        if (row[c] == null || row[c] === '') row[c] = master;
        filled[r] = row;
      }
    }
  }
  return filled;
}

function extractContactBlock(
  rows: Array<Array<string | number | null>>,
  targetSection: string,
  sectionCol: number,
  labelCol: number,
  valueCol: number,
  startRow: number,
): Contact {
  let inBlock = false;
  let activeSection = '';
  let name: string | null = null;
  let email: string | null = null;
  let phone: string | null = null;

  for (let i = startRow; i < rows.length; i++) {
    const raw = String(rows[i]?.[sectionCol] ?? '').trim();
    const blockSection = raw ? parseBlockSection(raw) : null;
    if (blockSection) activeSection = blockSection;

    if (activeSection === targetSection) inBlock = true;
    if (inBlock && blockSection && blockSection !== targetSection) break;

    if (!inBlock) continue;

    const label = normalizeSchedulingKey(String(rows[i]?.[labelCol] ?? ''));
    const value = String(rows[i]?.[valueCol] ?? '');
    if (label === 'name') name = value;
    if (isEmailLabel(label) && !email) {
      const parsed = parseWorkbookEmail(value);
      if (parsed) email = parsed;
    }
    if (isPhoneLabel(label)) phone = value;
  }

  return { name, email, phone };
}

export function extractPrimarySupportContact(sheet: XLSX.WorkSheet): Contact {
  const rows = expandMergedCells(XLSX.utils.sheet_to_json(sheet, { header: 1 }) as any[][], sheet);
  const cols = findPartnerContactColumns(rows);

  if (!cols) return { name: null, email: null, phone: null };

  const { headerRowIndex, sectionColumnIndex, labelColumnIndex, valueColumnIndex } = cols;
  const startRow = headerRowIndex + 1;
  const read = (section: string) =>
    extractContactBlock(rows, section, sectionColumnIndex, labelColumnIndex, valueColumnIndex, startRow);

  let contact = read(PRIMARY_SECTION);

  if (!contact.email) {
    for (const section of ESCALATION_FALLBACK) {
      const fallback = read(section);
      contact = {
        name: contact.name ?? fallback.name,
        email: contact.email ?? fallback.email,
        phone: contact.phone ?? fallback.phone,
      };
      if (contact.email) break;
    }
  }

  return { ...contact, phone: contact.phone ?? '' };
}
